import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  Heading,
  Box,
  Button,
  HStack,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Input,
  IconButton,
  Select
} from '@chakra-ui/react';
import SettingsBreadcrumb from '../../../components/SettingsBreadcrumb';
import Paginacao from '../../../components/common/paginacao/paginacao';
import * as api from '../../../api/evento';
import TabelaEvento from './tabelaEvento/tabela-evento';
import { AiOutlineClose, AiOutlineSearch } from 'react-icons/ai';
import { Dropdown, DropdownButton } from 'react-bootstrap';

interface Situacao {
  codSituacaoPublicacao: number;
  dscSituacaoPublicacao: string;
}

const GerenciarEvento = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [eventos, setEventos] = useState<any[]>([]);
  const [codTipoPublicacao, setcodTipoPublicacao] = useState(2);

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [pesquisa, setPesquisa] = useState<string>('');
  const [situacao, setSituacao] = useState<number | undefined>(undefined);
  const [dateFilterType, setDateFilterType] = useState<'publicacao' | 'despublicacao' | ''>('');
  const [pubInicio, setPubInicio] = useState<string>('');
  const [pubFim, setPubFim] = useState<string>('');
  const [despubInicio, setDespubInicio] = useState<string>('');
  const [despubFim, setDespubFim] = useState<string>('');
  const [estadoInputData, setEstadoInputData] = useState<'text' | 'date'>('text');

  const [situacoes, setSituacoes] = useState<Situacao[]>([]);
  const [loadingSituacoes, setLoadingSituacoes] = useState(false);
  const [errorSituacoes, setErrorSituacoes] = useState<string>();

  const formataData = (data: string) =>
    data ? data.split('-').reverse().join('/') : '';

  useEffect(() => {
    const fetchSituacoes = async () => {
      setLoadingSituacoes(true);
      try {
        const data = await api.obterListaFiltroSituacao();
        setSituacoes(data);
      } catch (err) {
        console.error(err);
        setErrorSituacoes('Falha ao carregar situações');
      } finally {
        setLoadingSituacoes(false);
      }
    };
    fetchSituacoes();
  }, []);

  useEffect(() => {
    const fetchEventos = async () => {
      setLoading(true);
      try {
        const datPubInicio = dateFilterType === 'publicacao' ? pubInicio : undefined;
        const datPubFim = dateFilterType === 'publicacao' ? pubFim : undefined;
        const datDespubInicio = dateFilterType === 'despublicacao' ? despubInicio : undefined;
        const datDespubFim = dateFilterType === 'despublicacao' ? despubFim : undefined;
        const codSituacaoParam = situacao !== undefined ? situacao : undefined;

        const data = await api.listarEventos(
          2,
          paginaAtual,
          itemsPerPage,
          pesquisa || undefined,
          codSituacaoParam,
          datPubInicio,
          datPubFim,
          datDespubInicio,
          datDespubFim
        );

        const lista = Array.isArray(data.conteudo)
          ? data.conteudo.map((n: any) => ({
            seqConteudo: n.seqConteudo ?? '',
            nomePublicacao: n.nomePublicacao ?? '',
            datRegistro: n.datRegistro ?? '',
            datPublicacao: n.datPublicacao ?? '',
            datDespublicar: n.datDespublicar ?? '',
            numRegistro: n.numRegistro ?? '',
            conteudoPaginaDTO: n.conteudoPaginaDTO ?? '',
            dscSituacaoPublicacao: n.dscSituacaoPublicacao ?? '',
          }))
          : [];

        setEventos(lista);
        setTotalPages(data.totalPaginas ?? 1);
      } catch (err) {
        console.error('Erro ao buscar eventos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEventos();
  }, [
    paginaAtual,
    itemsPerPage,
    pesquisa,
    situacao,
    dateFilterType,
    pubInicio, pubFim,
    despubInicio, despubFim,
  ]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [pesquisa]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [situacao, dateFilterType, pubInicio, pubFim, despubInicio, despubFim]);

  const handleDeleteEvento = (seqEventoDeletado: number) => {
    setEventos((prevEvento) =>
      prevEvento.filter((evento) => evento.seqConteudo !== seqEventoDeletado)
    );
  };

  return (
    <Flex p="40px" bgColor="#fafafa" direction="column" w="100%">
      <SettingsBreadcrumb />
      <Heading mt="30px" fontWeight="600" fontSize="21px" color="#6F6F6F" w="100%">
        Gerenciar Eventos
      </Heading>

      <Flex
        w="100%"
        mt="40px"
        mb="20px"
        align="flex-start"
        justify="space-between"
        flexWrap="wrap"
        gap="16px"
      >
        <Button
          color="#fff"
          bgColor="#1789FC"
          _hover={{
            bgColor: "#1468C9"
          }}
          onClick={() => navigate('criar-evento')}
        >
          Novo Evento
        </Button>

        <Box flex="1" minW="280px">
          <InputGroup mb="12px">
            <InputLeftElement pointerEvents="none">
              <AiOutlineSearch color="#6e6e6e" />
            </InputLeftElement>
            <Input
              bg="white"
              placeholder="Pesquisar por título..."
              value={pesquisa}
              onChange={e => setPesquisa(e.target.value)}
            />
            {pesquisa && (
              <InputRightElement>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Limpar busca"
                  icon={<AiOutlineClose />}
                  onClick={() => setPesquisa('')}
                />
              </InputRightElement>
            )}
          </InputGroup>

          <Flex wrap="wrap" gap="12px" justifyContent="flex-end">
            <DropdownButton
            id="dropdown-situacao"
            size="sm"
            title={
              situacao
                ? situacoes.find(s => s.codSituacaoPublicacao === situacao)?.dscSituacaoPublicacao
                : "Situação"
            }
            className="custom-dropdown"
          >
            {situacoes.map(s => (
              <Dropdown.Item
                key={s.codSituacaoPublicacao}
                onClick={() => setSituacao(s.codSituacaoPublicacao)}
              >
                {s.dscSituacaoPublicacao}
              </Dropdown.Item>
            ))}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => setSituacao(undefined)}>
              Limpar filtro
            </Dropdown.Item>
          </DropdownButton>

          <DropdownButton
            id="dropdown-periodo"
            size="sm"
            title={
              dateFilterType === "publicacao"
                ? "Data de publicação"
                : dateFilterType === "despublicacao"
                ? "Data de despublicação"
                : "Período"
            }
            className="custom-dropdown"
          >
            <Dropdown.Item onClick={() => setDateFilterType("publicacao")}>
              Data de publicação
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setDateFilterType("despublicacao")}>
              Data de despublicação
            </Dropdown.Item>

            <Dropdown.Divider />

            <Dropdown.Item onClick={() => setDateFilterType("")}>
              Limpar período
            </Dropdown.Item>
          </DropdownButton>

            {dateFilterType && (
              <>
                <Input
                  type={estadoInputData}
                  size="sm"
                  w="160px"
                  value={
                    estadoInputData === 'date'
                      ? (dateFilterType === 'publicacao' ? pubInicio : despubInicio)
                      : (dateFilterType === 'publicacao'
                        ? formataData(pubInicio)
                        : formataData(despubInicio))
                  }
                  onChange={e => {
                    if (estadoInputData === 'date') {
                      return dateFilterType === 'publicacao'
                        ? setPubInicio(e.target.value)
                        : setDespubInicio(e.target.value);
                    }
                  }}
                  onFocus={() => setEstadoInputData('date')}
                  onBlur={() => setEstadoInputData('text')}
                  placeholder="De"
                  color="#6e6e6e"
                  readOnly={estadoInputData === 'text'}
                  _placeholder={{ color: '#6e6e6e' }}
                />

                <Input
                  type={estadoInputData}
                  size="sm"
                  w="160px"
                  value={
                    estadoInputData === 'date'
                      ? (dateFilterType === 'publicacao' ? pubFim : despubFim)
                      : (dateFilterType === 'publicacao'
                        ? formataData(pubFim)
                        : formataData(despubFim))
                  }
                  onChange={e => {
                    if (estadoInputData === 'date') {
                      return dateFilterType === 'publicacao'
                        ? setPubFim(e.target.value)
                        : setDespubFim(e.target.value);
                    }
                  }}
                  onFocus={() => setEstadoInputData('date')}
                  onBlur={() => setEstadoInputData('text')}
                  placeholder="Até"
                  color="#6e6e6e"
                  readOnly={estadoInputData === 'text'}
                  _placeholder={{ color: '#6e6e6e' }}
                />
              </>
            )}

            {(situacao !== undefined ||
              dateFilterType ||
              pubInicio ||
              pubFim ||
              despubInicio ||
              despubFim) && (
                <IconButton
                  size="sm"
                  variant="outline"
                  aria-label="Limpar filtros"
                  icon={<AiOutlineClose />}
                  onClick={() => {
                    setSituacao(undefined);
                    setDateFilterType('');
                    setPubInicio('');
                    setPubFim('');
                    setDespubInicio('');
                    setDespubFim('');
                  }}
                />
              )}
          </Flex>
        </Box>
      </Flex>

      <Box position="relative" mt={5}>
        <TabelaEvento
          listaEvento={eventos}
          listaVazia="Nenhum dado disponível."
          loading={loading}
          onDelete={handleDeleteEvento}
        />
      </Box>

      <Paginacao
        paginaAtual={paginaAtual}
        totalPages={totalPages}
        onPageChange={setPaginaAtual}
      />
    </Flex>
  );
};

export default GerenciarEvento;
